import { useEffect, useState } from 'react';
import axios from 'axios';
import Reveal from '../components/site/Reveal';
import SectionHeader from '../components/site/SectionHeader';
import { PRINCIPLES, COMMITMENTS } from '../data/site';
import { Target, Compass, Crown, Linkedin, ArrowUpRight, Quote } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';
import { useHeroImages } from '../lib/useHeroImages';
import { useExternalLink } from '../contexts/ExternalLinkContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_ABOUT_HERO = 'https://images.pexels.com/photos/35058546/pexels-photo-35058546.jpeg?auto=compress&cs=tinysrgb&w=1600';

export default function About() {
  const { t } = useT();
  const heroes = useHeroImages();
  const { confirm } = useExternalLink();
  const [team, setTeam] = useState({ management: [], advisory: [] });

  useEffect(() => {
    axios.get(`${API}/team`).then((r) => {
      const list = r.data || [];
      setTeam({
        management: list.filter((m) => m.group === 'management'),
        advisory: list.filter((m) => m.group === 'advisory'),
      });
    }).catch(() => {});
  }, []);

  const pillars = [
    { icon: Target, title: t('about.mission'), text: 'Provide growth services that help run business, while minimizing obstacles so entrepreneurs can focus on developing their ideas.' },
    { icon: Compass, title: t('about.vision'), text: 'Work with talented and outstanding entrepreneurs to develop management teams and build substantial business enterprises around them.' },
    { icon: Crown, title: t('about.edge'), text: 'Entrepreneurs do not view us as typical venture capitalists. They view us as partners and fellow executives — helping them build market-leading companies.' },
  ];

  return (
    <div data-testid="page-about">
      <section className="pt-40 pb-20 relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6">{t('about.eyebrow')}</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-prim max-w-4xl">
              {t('about.title')}
            </h1>
            <p className="mt-8 text-sec text-lg md:text-xl max-w-3xl leading-relaxed">
              {t('about.intro')}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div className="surface-2 border border-subtle p-8 md:p-10 rounded-md card-glow h-full">
                  <div className="w-12 h-12 rounded-full bg-brand-maroon/30 border border-brand-maroon-light/40 flex items-center justify-center">
                    <p.icon size={20} className="text-brand-teal-light" />
                  </div>
                  <h3 className="font-display text-3xl text-prim mt-6 tracking-tight">{p.title}</h3>
                  <p className="mt-4 text-sec leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER & CHAIRMAN */}
      <section data-testid="about-founder-section" className="py-24 md:py-32 border-t border-subtle relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-brand-maroon/10 blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 w-[500px] h-[500px] rounded-full bg-brand-teal/10 blur-[120px] pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6">— Founder & Chairman</p>
          </Reveal>

          {/* Pull quote */}
          <Reveal delay={120}>
            <figure className="max-w-4xl">
              <Quote size={42} className="text-brand-teal-light/60" />
              <blockquote className="mt-5 font-display text-2xl md:text-3xl lg:text-4xl text-prim leading-[1.25] tracking-tight">
                <span className="text-brand-teal-light">“</span>The future belongs to those who not only embrace innovation but actively innovate innovation itself. Our mission is to build a global ecosystem where talented entrepreneurs are empowered with the strategic capital and mentorship required to transform groundbreaking ideas into market-leading enterprises, thereby driving sustainable economic growth for the region and the world.<span className="text-brand-teal-light">”</span>
              </blockquote>
              <figcaption className="mt-8 flex items-center gap-3 text-sec">
                <span className="h-px w-10 bg-brand-teal-light/60" />
                <span className="font-mono text-xs tracking-[0.25em] uppercase">H.E. Sheikh Mansoor Bin Khalifa Al Thani</span>
              </figcaption>
            </figure>
          </Reveal>

          {/* Bio + portfolio CTA */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <Reveal className="lg:col-span-7" delay={200}>
              <p className="font-arabic text-3xl text-brand-teal-light/80 mb-5">مَعَالِي الشَّيْخ</p>
              <h2 className="font-display text-3xl md:text-4xl text-prim tracking-tight leading-tight">
                H.E. Sheikh Mansoor Bin Khalifa Al Thani
              </h2>
              <p className="mt-2 font-mono text-[11px] tracking-[0.3em] uppercase text-brand-teal-light">
                Founder & Chairman · MBK Holding
              </p>
              <p className="mt-6 text-sec text-lg leading-relaxed max-w-2xl">
                Visionary leader, innovator and catalyst for Qatar's digital future. Through MBK Holding, H.E. Sheikh Mansoor Bin Khalifa Al Thani is advancing technology, innovation and entrepreneurship in direct alignment with Qatar National Vision 2030 — building the operating companies, capital architecture and partnerships that will define the country's next generation of industry.
              </p>
              <a
                href="https://www.mansoorbinkhalifa.com"
                onClick={(e) => { e.preventDefault(); confirm('https://www.mansoorbinkhalifa.com', 'mansoorbinkhalifa.com'); }}
                data-testid="founder-portfolio-link"
                className="btn-secondary mt-8"
              >
                Visit personal portfolio <ArrowUpRight size={14} />
              </a>
            </Reveal>

            <Reveal className="lg:col-span-5" delay={300}>
              <div className="relative shimmer-border p-1">
                <div className="aspect-[4/5] rounded-xl overflow-hidden bg-gradient-to-br from-brand-maroon/30 via-[#0F172A] to-brand-teal/20 flex items-center justify-center p-10 text-center">
                  <div>
                    <p className="font-arabic text-6xl text-brand-teal-light/80">مَنْصُور</p>
                    <p className="mt-6 font-display text-2xl text-prim tracking-tight">H.E. Sheikh Mansoor</p>
                    <p className="font-display text-lg text-sec tracking-tight">Bin Khalifa Al Thani</p>
                    <div className="mt-6 h-px w-20 bg-brand-teal-light/60 mx-auto" />
                    <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-brand-teal-light">
                      Vision 2030 · Patronage
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-subtle relative">
        <div className="absolute inset-0 z-0">
          <img src={heroes.about || DEFAULT_ABOUT_HERO} alt="" className="w-full h-full object-cover hero-img" />
          <div className="absolute inset-0" style={{ background: 'var(--hero-overlay)' }} />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionHeader eyebrow="— Philosophy" title="Three virtues, equal weight." />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 120}>
                <div className="surface-2 border border-subtle p-8 rounded-md card-glow h-full">
                  <p className="font-arabic text-5xl text-brand-teal-light/80">{p.arabic}</p>
                  <h3 className="font-display text-2xl text-prim mt-6 tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sec leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionHeader eyebrow="— Leadership" title={t('about.leadership')} />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.management.map((m, i) => (
              <Reveal key={m.id} delay={i * 100}>
                <div data-testid={`leader-${i}`} className="surface-2 border border-subtle p-6 rounded-md card-glow h-full">
                  <div className="aspect-square rounded-md overflow-hidden bg-gradient-to-br from-brand-maroon/40 to-brand-teal/20 flex items-center justify-center">
                    {m.photo_base64 ? (
                      <img src={m.photo_base64} alt={m.name} className="photo-fit" />
                    ) : (
                      <span className="font-display text-5xl text-prim/30">{m.name.split(' ').map(n => n[0]).slice(0,2).join('')}</span>
                    )}
                  </div>
                  {m.arabic && <p className="font-arabic text-lg text-brand-teal-light/70 mt-5">{m.arabic}</p>}
                  <h4 className="font-display text-lg text-prim mt-2 tracking-tight leading-snug">{m.name}</h4>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-brand-teal-light">{m.role}</p>
                  {m.linkedin && (
                    <a href={m.linkedin} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-brand-teal-light hover-line">
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <p className="eyebrow mt-24 mb-8">— {t('about.advisoryBoard')}</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {team.advisory.map((a, i) => (
                <div key={a.id} data-testid={`advisor-${i}`} className="surface-2 border border-subtle p-6 rounded-md text-center card-glow">
                  <div className="w-16 h-16 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-brand-maroon/40 to-brand-teal/20 flex items-center justify-center">
                    {a.photo_base64 ? <img src={a.photo_base64} alt={a.name} className="photo-fit" /> : <span className="font-display text-xl text-prim/60">{a.name.split(' ').map(n=>n[0]).slice(0,2).join('')}</span>}
                  </div>
                  <p className="mt-4 font-display text-base text-prim tracking-tight">{a.name}</p>
                  {a.linkedin && <a href={a.linkedin} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-xs text-brand-teal-light"><Linkedin size={11} /></a>}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <SectionHeader eyebrow={`— ${t('about.threeCommitments')}`} title="How we deliver against the vision." />
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.title} delay={i * 120}>
                <div className="surface-2 border border-subtle p-8 rounded-md card-glow h-full">
                  <p className="font-arabic text-4xl text-brand-teal-light/80">{c.arabic}</p>
                  <h4 className="font-display text-xl text-prim mt-5 tracking-tight">{c.title}</h4>
                  <p className="mt-3 text-sec">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
